import * as React from 'react'
import Nestable, { RenderItem } from 'react-nestable'
import { History } from 'history'
import { connect } from 'react-redux'
import { Localized } from '@fluent/react'
import { Book, BookPart, Module, Process } from '../../../api'
import { GroupData, ModuleData, PartData } from '../../../api/bookpart'
import store from '../../../store'
import * as types from '../../../store/types'
import { State } from '../../../store/reducers'
import { removeExpandedGroup, setExpandedGroup } from '../../../store/actions/app'
import { addAlert } from '../../../store/actions/alerts'
import { confirmDialog, escapeStringRegExp } from '../../../helpers'
import Section from '../../../components/Section'
import Header from '../../../components/Header'
import Spinner from '../../../components/Spinner'
import LimitedUI from '../../../components/LimitedUI'
import EditBook from '../../../components/EditBook'
import BookPartGroup from '../../../components/BookPartGroup'
import BookPartModule from '../../../components/BookPartModule'
import ProcessSelector from '../../../components/ProcessSelector'
import SearchInput, { SearchQueries } from '../../../components/SearchInput'
import LockToggle from '../../../components/LockToggle'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/ui/Icon'
import Dialog from '../../../components/ui/Dialog'
import Input from '../../../components/ui/Input'
import ModulePreview from '../../../containers/ModulePreview'
import ModulesPicker from '../../../containers/ModulesPicker'
import ExportButton from './components/ExportButton'

import './index.css'

interface BookProps {
  match: {
    params: {
      id: string
    }
  }
  history: History
  modules: {
    labels: types.Labels
    modulesWithLabels: types.ModulesWithLabels
  }
  expandedGroups: types.ExpandedGroups
}

const mapStateToProps = ({ app, modules }: State) => ({
  expandedGroups: app.expandedGroups,
  modules,
})

interface BookState {
  isLoading: boolean
  book?: Book
  parts: BookPart[]
  showModuleDetails: Module | undefined
  showEditBook: boolean
  isEditingUnlocked: boolean
  isChangingIdUnlocked: boolean
  groupNameInput: string
  showAddGroup: boolean
  parentForNewGroup: BookPart | null
  showAddModule: boolean
  parentForNewModule: BookPart | null
  search: SearchQueries
  selectedProcess: Process | null
}

class BookComponent extends React.Component<BookProps> {
  state: BookState = {
    isLoading: true,
    parts: [],
    showModuleDetails: undefined,
    showEditBook: false,
    isEditingUnlocked: false,
    isChangingIdUnlocked: false,
    groupNameInput: '',
    showAddGroup: false,
    parentForNewGroup: null,
    showAddModule: false,
    parentForNewModule: null,
    search: { text: '' },
    selectedProcess: null,
  }

  private handleProcessChange = (option: { value: Process } | null) => {
    this.setState({ selectedProcess: option?.value || null })
  }

  private onSearch = (search: SearchQueries) => {
    this.setState({ search })
  }

  private showModuleDetails = async (item: BookPart) => {
    if (item.kind === 'module' && item.id) {
      const details = await Module.load(item.id)
      this.setState({ showModuleDetails: details })
    }
  }

  private closeModuleDetails = () => {
    this.setState({ showModuleDetails: undefined })
  }

  private filterByLabel = (label: types.ModuleLabel) => {
    // Set new label in search input or remove it if it was clicked two times.
    this.setState((state: BookState) => ({
      search: {
        text: state.search.text,
        label: state.search.label && state.search.label.id === label.id ? undefined : label,
      },
    }))
  }

  /**
   * Check if @param {ModuleData} mod is matching searchInput and / or selectedProcess.
   */
  private testModule = (mod: ModuleData) => {
    const { selectedProcess, search } = this.state
    const { modules: { modulesWithLabels } } = this.props

    const rgx = new RegExp(escapeStringRegExp(search.text), 'gi')

    let passed = true
    if (selectedProcess) {
      if (!mod.process || mod.process.process !== selectedProcess.id) {
        passed = false
      }
    }
    if (passed && search.label) {
      if (
        !modulesWithLabels[mod.id]
        || modulesWithLabels[mod.id].indexOf(search.label.id) === -1
      ) {
        passed = false
      }
    }
    if (passed && search.text) {
      if (!mod.title.match(rgx)) {
        passed = false
      }
    }
    return passed
  }

  /**
   * Check if any of modules in @param {GroupData} part is matching
   * searchInput and / or selectedProcess.
   */
  private testModulesInPart = (
    part: GroupData,
    cb: (mod: ModuleData) => boolean,
  ): boolean => part.parts.some(part => {
    if (part.kind === 'module' && cb(part)) return true
    if (part.kind === 'group') return this.testModulesInPart(part, cb)
    return false
  })

  private toggleCollapseGroup = (num: number) => {
    this.nestable.current!.toggleCollapseGroup(num)
  }

  private onToggleCollapse = (num: number, newState: boolean) => {
    // Do not save toggle / collapse when results are filtered because then
    // they are automatically expanded.
    const { search: { text, label } } = this.state
    if (text || label) return

    const url = window.location.pathname
    if (newState) {
      store.dispatch(setExpandedGroup(url, num))
    } else {
      store.dispatch(removeExpandedGroup(url, num))
    }
  }

  private renderItem = ({ item, collapseIcon }: RenderItem<PartData>) => {
    const { selectedProcess, search, isEditingUnlocked, book } = this.state

    if (selectedProcess || search.text || search.label) {
      if (item.kind === 'module' && !this.testModule(item)) return null
      if (
        item.kind === 'group' &&
        !this.testModulesInPart(item as GroupData, this.testModule)
      ) return null
    }

    return (
      <div data-testid='bookpart_item' className={`bookpart__item bookpart__item--${item.kind}`}>
        {
          item.kind === 'group' ?
            <BookPartGroup
              item={new BookPart(item as GroupData, book!)}
              book={book!}
              collapseIcon={collapseIcon}
              afterAction={this.fetchBook}
              isEditingUnlocked={isEditingUnlocked}
              showStatsFor={selectedProcess}
              onTitleClick={this.toggleCollapseGroup}
              onModuleAdd={this.showAddModuleDialog}
              onGroupAdd={this.showAddGroupDialog}
            />
            :
            <BookPartModule
              item={new BookPart(item as ModuleData, book!)}
              onModuleClick={this.showModuleDetails}
              afterAction={this.fetchBook}
              isEditingUnlocked={isEditingUnlocked}
              isChangingIdUnlocked={this.state.isChangingIdUnlocked}
              highlightText={search.text}
              onModuleLabelClick={this.filterByLabel}
            />
        }
      </div>
    )
  }

  private renderCollapseIcon = ({ isCollapsed }: {isCollapsed: boolean}) => {
    if (isCollapsed) {
      return <Icon size="medium" name="arrow-right"/>
    }

    return <Icon size="medium" name="arrow-down" />
  }

  private findParentWithinItems = (items: BookPart[], path: number[]) => {
    const pathToParent = [...path]
    pathToParent.pop() // remove last index because it's pointing to changedItem

    let parent: BookPart

    if (pathToParent.length === 0) {
      parent = this.state.parts[0]
    } else {
      parent = items[pathToParent[0]]
      pathToParent.shift()

      // If there are still some indexes then process them
      if (pathToParent.length > 0) {
        pathToParent.forEach(index => {
          if (parent && parent.parts && parent.parts[index]) {
            parent = parent.parts[index]
          } else {
            throw new Error(`Couldn't find parent for item at path: ${JSON.stringify(path)}`)
          }
        })
      }
    }

    return parent
  }

  private handleOnMove = (
    newItems: BookPart[],
    changedItem: BookPart,
    realPathTo: number[],
  ) => {
    // Do not move bookparts into modules
    // TODO: Create new part when user move module into module
    const parent: BookPart | Book = this.findParentWithinItems(newItems, realPathTo)

    if (typeof (parent as BookPart).number !== 'number') {
      console.warn('You can not move items outside book.')
      return false
    } else if ((parent as BookPart).kind === 'module') {
      console.warn('You can not move modules into modules.')
      return false
    }

    return true
  }

  private handlePositionChange = (
    newItems: BookPart[],
    changedItem: BookPart,
    realPathTo: number[],
  ) => {
    const targetParent = this.findParentWithinItems(newItems, realPathTo)
    const targetPosition = {
      parent: targetParent.number,
      index: realPathTo[realPathTo.length - 1],
    }

    if (!(changedItem instanceof BookPart)) {
      // While processing data trough react-nestable, instances are lost,
      // so we have to recreate it
      // TODO: Adjust / rewrite react-nestable to handle instances properly
      changedItem = new BookPart(changedItem as PartData, this.state.book!)
    }

    changedItem.update(targetPosition)
      .then(() => {
        this.fetchBook()
        store.dispatch(addAlert(
          'success',
          'book-part-moving-alert-success',
          { item: changedItem.title, target: targetParent.title },
        ))
      })
      .catch(e => {
        store.dispatch(addAlert('error', e.message))
        this.fetchBook()
      })
  }

  private fetchBook = (id: string = this.props.match.params.id) => {
    Book.load(id)
      .then(book => {
        this.setState({ book })
        book.parts()
          .then(parts => {
            this.setState({ isLoading: false, parts: [parts] })
          })
          .catch(e => {
            this.setState({ isLoading: false })
            store.dispatch(addAlert(
              'error',
              'book-fetch-error',
              { title: book.title, details: e.message },
            ))
          })
      })
      .catch(() => {
        this.props.history.push('/404')
      })
  }

  private showEditBook = () => {
    this.setState({ showEditBook: true })
  }

  private closeEditBook = () => {
    this.setState({ showEditBook: false })
  }

  private toggleEditing = (status: boolean) => {
    this.setState({ isEditingUnlocked: status })
  }

  private handleAddGroup = (e: React.FormEvent) => {
    e.preventDefault()

    const { book, groupNameInput, parentForNewGroup } = this.state

    if (!groupNameInput.length || !parentForNewGroup) {
      throw new Error("groupNameInput or parentForNewGroup is undefined")
    }

    const payload = {
      title: groupNameInput,
      parent: parentForNewGroup.number,
      index: parentForNewGroup.parts ? parentForNewGroup.parts.length : 0,
      parts: [],
    }

    book!.createPart(payload)
      .then(() => {
        this.fetchBook()
        store.dispatch(addAlert('success', 'book-add-group-alert-success'))
      })
      .catch(e => {
        store.dispatch(addAlert('error', e.message))
      })
    this.closeAddGroupDialog()
  }

  private handleAddGroupClick = () => {
    this.showAddGroupDialog(this.state.parts[0])
  }

  private showAddGroupDialog = (parentForNewGroup: BookPart) => {
    this.setState({ showAddGroup: true, parentForNewGroup })
  }

  private closeAddGroupDialog = () => {
    this.setState({ showAddGroup: false, parentForNewGroup: null })
  }

  private updateGroupNameInput = (val: string) => {
    this.setState({ groupNameInput: val })
  }

  private addExistingModule = async (selectedModule: Module) => {
    const { book, parentForNewModule } = this.state

    if (!selectedModule || !parentForNewModule) {
      throw new Error("selectedModule or parentForNewModule is undefined")
    }

    if (await book!.hasBookPart(selectedModule.id)) {
      const res = await confirmDialog({
        title: 'book-group-add-module-already-in-book',
        size: 'medium',
        showCloseButton: false,
        buttons: {
          cancel: 'book-group-add-module-already-in-book-cancel',
          add: 'book-group-add-module-already-in-book-add-anyway',
        },
      })

      if (res === 'cancel') {
        this.closeAddModuleDialog()
        return
      }
    }

    const payload = {
      module: selectedModule.id,
      parent: parentForNewModule.number,
      index: parentForNewModule.parts ? parentForNewModule.parts.length : 0,
    }

    book!.createPart(payload)
      .then(() => {
        this.fetchBook()
        store.dispatch(addAlert('success', 'book-group-add-module-alert-success', {
          title: selectedModule.title,
        }))
      })
      .catch(e => {
        store.dispatch(addAlert('error', e.message))
      })
    this.closeAddModuleDialog()
  }

  private addNewModule = (newModule: Module) => {
    const { book, parentForNewModule } = this.state

    if (!newModule || !parentForNewModule) {
      throw new Error("newModule or parentForNewModule is undefined")
    }

    const payload = {
      module: newModule.id,
      parent: parentForNewModule.number,
      index: parentForNewModule.parts ? parentForNewModule.parts.length : 0,
    }

    book!.createPart(payload)
      .then(() => {
        this.fetchBook()
        store.dispatch(addAlert('success', 'book-group-add-module-alert-success', {
          title: newModule.title,
        }))
      })
      .catch(e => {
        store.dispatch(addAlert('error', e.message))
      })
    this.closeAddModuleDialog()
  }

  private handleAddModuleClick = () => {
    this.showAddModuleDialog(this.state.parts[0])
  }

  private showAddModuleDialog = (parentForNewModule: BookPart) => {
    this.setState({ showAddModule: true, parentForNewModule })
  }

  private closeAddModuleDialog = () => {
    this.setState({ showAddModule: false, parentForNewModule: null })
  }

  private showChangeIdOption = () => {
    // eslint-disable-next-line
    this.setState({ isChangingIdUnlocked: !this.state.isChangingIdUnlocked })
  }

  private exportModule = () => {
    this.state.showModuleDetails!.export()
  }

  componentDidMount() {
    this.fetchBook()
  }

  nestable = React.createRef<Nestable>()

  public render() {
    const { expandedGroups } = this.props
    const {
      isLoading,
      book,
      parts,
      showModuleDetails,
      isEditingUnlocked,
      showEditBook,
      showAddGroup,
      showAddModule,
      groupNameInput,
      search,
    } = this.state

    const title = book ? book.title : 'Loading'
    const titleKey = book ? 'book-view-title' : 'book-view-title-loading'

    return (
      <div className="container container--splitted">
        {
          showEditBook ?
            <EditBook
              book={book!}
              onClose={this.closeEditBook}
            />
            : null
        }
        <Section className="book">
          <Header l10nId={titleKey} title={title}>
            <LimitedUI permissions="book:edit">
              <LockToggle onToggle={this.toggleEditing} />
              {
                isEditingUnlocked ?
                  <Button
                    id="book-toggle-edit"
                    clickHandler={this.showEditBook}
                  >
                    <Icon name="pencil"/>
                  </Button>
                  : null
              }
            </LimitedUI>
            <ExportButton book={book} />
            {this.state.isEditingUnlocked &&
              <Button
                l10nId="book-show-alternative-id"
                clickHandler={this.showChangeIdOption}
                withBorder={true}
              />}
            <div className="book__search">
              <SearchInput
                value={search}
                placeholder="book-search-input"
                slowMode={true}
                onChange={this.onSearch}
              />
            </div>
          </Header>
          <ProcessSelector
            title="book-statistics-choose-process"
            team={book ? book.team : undefined}
            onChange={this.handleProcessChange}
          />
          {
            !isLoading ?
              <>
                <Nestable
                  ref={this.nestable}
                  isDisabled={!isEditingUnlocked}
                  items={parts[0].parts as BookPart[]}
                  className="book-collection"
                  childrenProp="parts"
                  renderItem={this.renderItem}
                  renderCollapseIcon={this.renderCollapseIcon}
                  onMove={this.handleOnMove}
                  onChange={this.handlePositionChange}
                  collapsed={!search.text && !search.label}
                  collapsedGroups={expandedGroups[window.location.pathname] || []}
                  onToggleCollapse={this.onToggleCollapse}
                />
                {
                  isEditingUnlocked ?
                    <div className="book__controls">
                      <LimitedUI permissions="book:edit">
                        <Button l10nId="book-button-add-module" clickHandler={this.handleAddModuleClick}>
                          Add module
                        </Button>
                        <Button l10nId="book-button-add-group" clickHandler={this.handleAddGroupClick}>
                          Add group
                        </Button>
                      </LimitedUI>
                    </div>
                    : null
                }
              </>
              : <Spinner />
          }
        </Section>
        {
          showModuleDetails ?
            <Section className='module-details'>
              <div className="book-module-details-wrapper">
                <Header>
                  <Button l10nId="module-go-to" to={`/modules/${showModuleDetails.id}`} withBorder={true}>
                    Go to module
                  </Button>
                  <LimitedUI team={showModuleDetails.team} permissions="content:export">
                    <Button l10nId="module-export" clickHandler={this.exportModule} withBorder={true}>
                      Export
                    </Button>
                  </LimitedUI>
                  <Button
                    id="module-close"
                    clickHandler={this.closeModuleDetails}
                    className="close-button-module-preview"
                  >
                    <Icon name="close" />
                  </Button>
                </Header>
                <div className="section__content">
                  <ModulePreview moduleId={showModuleDetails.id}/>
                </div>
              </div>
            </Section>
            : null
        }
        {
          showAddGroup ?
            <Dialog
              l10nId="book-add-group-dialog-title"
              placeholder="Provide chapter title."
              size="medium"
              onClose={this.closeAddGroupDialog}
            >
              <form onSubmit={this.handleAddGroup}>
                <Input
                  type="text"
                  l10nId="book-add-group-title"
                  onChange={this.updateGroupNameInput}
                  autoFocus
                  minLength={3}
                  required
                />
                <div className="dialog__buttons">
                  <Button l10nId="book-add-group-cancel" clickHandler={this.closeAddGroupDialog}>
                    Cancel
                  </Button>
                  <Localized id="book-add-group-confirm" attrs={{ value: true }}>
                    <input
                      type="submit"
                      value="Confirm"
                      disabled={groupNameInput.length <= 2}
                    />
                  </Localized>
                </div>
              </form>
            </Dialog>
            : null
        }
        {
          showAddModule ?
            <Dialog
              l10nId="book-group-add-module-dialog-title"
              placeholder="Select module or create a new one."
              size="medium"
              onClose={this.closeAddModuleDialog}
            >
              <ModulesPicker
                team={book!.team}
                filterByTeam={book!.team}
                filterByBook={true}
                filterByBookInitialValue={book!}
                onModuleClick={this.addExistingModule}
                onModuleAdd={this.addNewModule}
              />
            </Dialog>
            : null
        }
      </div>
    )
  }
}

export default connect(mapStateToProps)(BookComponent)
